// MarkdownDiffView — the Documents diff surface (proposal change documents +
// any changed `.md`). Left: a picker of every `.md` that differs between the
// before/after VFS. Right: the selected doc's before → after panes, rendered
// read-only through the same CM6 live-preview the editor uses (mounted directly
// via `mountMarkdown` against raw strings — no WorkspaceStore, so it composes
// as a pure before/after view). Mirrors ProcessDiffView's shape.

import { useEffect, useMemo, useRef, useState } from "react";
import { isMarkdownPath, type Vfs } from "@archlang/viewer-core";
import { useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { useTranslation } from "../i18n";
import type { MdController } from "../workbench/primitives/md-editor/bundle.js";

export interface MarkdownDiffViewProps {
  readonly before: Vfs;
  readonly after: Vfs;
}

type DocStatus = "added" | "removed" | "modified";

interface DocChange {
  readonly path: string;
  readonly status: DocStatus;
  readonly before: string | null;
  readonly after: string | null;
}

const STATUS_CHIP: Record<DocStatus, string> = {
  added: "bg-success/[0.14] text-success border-success/30",
  removed: "bg-danger/[0.14] text-danger border-danger/30",
  modified: "bg-warning/[0.14] text-warning border-warning/30",
};

function changedDocs(before: Vfs, after: Vfs): DocChange[] {
  const paths = new Set<string>();
  for (const p of before.keys()) if (isMarkdownPath(p)) paths.add(p);
  for (const p of after.keys()) if (isMarkdownPath(p)) paths.add(p);

  const out: DocChange[] = [];
  for (const path of paths) {
    const b = before.has(path) ? before.get(path)! : null;
    const a = after.has(path) ? after.get(path)! : null;
    if (b === a) continue; // unchanged
    const status: DocStatus = b === null ? "added" : a === null ? "removed" : "modified";
    out.push({ path, status, before: b, after: a });
  }
  return out.sort((x, y) => x.path.localeCompare(y.path));
}

/** A read-only CM6 markdown render of a raw string (no store binding). */
function ReadonlyMarkdown({ content }: { content: string | null }) {
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (content === null) return;
    let alive = true;
    let ctrl: MdController | null = null;
    const host = hostRef.current;
    if (!host) return;
    void import("../workbench/primitives/md-editor/bundle.js").then(({ mountMarkdown }) => {
      if (!alive || !hostRef.current) return;
      ctrl = mountMarkdown(hostRef.current, {
        doc: content,
        editable: false,
        onChange: () => {},
      });
    });
    return () => {
      alive = false;
      ctrl?.destroy();
    };
  }, [content]);

  if (content === null) {
    return (
      <div className="flex h-full items-center justify-center text-sm italic text-slate-600">
        — absent —
      </div>
    );
  }
  return <div ref={hostRef} className="arch-selectable h-full overflow-auto" />;
}

export function MarkdownDiffView({ before, after }: MarkdownDiffViewProps) {
  const { t } = useTranslation("nav");
  const menu = useContextMenu();
  const docs = useMemo(() => changedDocs(before, after), [before, after]);
  const [selectedPath, setSelectedPath] = useState<string | null>(docs[0]?.path ?? null);
  const selected = useMemo(
    () => docs.find((d) => d.path === selectedPath) ?? docs[0] ?? null,
    [docs, selectedPath],
  );

  const menuItemsFor = (d: DocChange): ContextMenuItem[] => [
    { label: t("menu.open"), onClick: () => setSelectedPath(d.path) },
    {
      label: t("menu.copyName"),
      onClick: () => void navigator.clipboard?.writeText(d.path.split("/").pop() ?? d.path),
    },
    { label: t("menu.copyPath"), onClick: () => void navigator.clipboard?.writeText(d.path) },
  ];

  if (docs.length === 0) {
    return (
      <div
        className="flex h-full items-center justify-center text-sm text-slate-500"
        data-testid="doc-diff-empty"
      >
        No document changes.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0" data-testid="doc-diff">
      <aside className="w-[260px] shrink-0 overflow-y-auto border-r border-slate-800 bg-slate-900/(--tint-strong)">
        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
          Changed documents
        </div>
        <ul>
          {docs.map((d) => (
            <li key={d.path}>
              <button
                type="button"
                data-doc-pick={d.path}
                onClick={() => setSelectedPath(d.path)}
                onContextMenu={(e) => menu.open(e, menuItemsFor(d))}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${
                  selected?.path === d.path
                    ? "bg-sky-400/[0.12] text-white"
                    : "text-slate-300 hover:bg-slate-400/[0.06]"
                }`}
              >
                <span className="truncate flex-1" title={d.path}>
                  {d.path.split("/").pop()}
                </span>
                <span className={`px-1.5 py-px rounded-full border text-[10px] ${STATUS_CHIP[d.status]}`}>
                  {d.status}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      {selected && (
        <div className="flex-1 min-w-0 min-h-0 grid grid-cols-2">
          <div className="flex min-h-0 flex-col border-r border-slate-800">
            <div className="px-3 py-1.5 border-b border-slate-800 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
              before
            </div>
            <div className="flex-1 min-h-0">
              <ReadonlyMarkdown key={`b:${selected.path}`} content={selected.before} />
            </div>
          </div>
          <div className="flex min-h-0 flex-col">
            <div className="px-3 py-1.5 border-b border-slate-800 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
              after
            </div>
            <div className="flex-1 min-h-0">
              <ReadonlyMarkdown key={`a:${selected.path}`} content={selected.after} />
            </div>
          </div>
        </div>
      )}
      {menu.node}
    </div>
  );
}
